'use client'

import type { AriaAttributes, KeyboardEvent as ReactKeyboardEvent } from 'react'
import type {
  GAutocompleteSuggestion,
  ParsedAddress,
  PlacesLib,
} from './google-places'
import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '../cn'
import { loadPlacesLibrary, parsePlace } from './google-places'

// Mirrors the shadcn Input primitive so the field looks identical across apps
// (apps share the same Tailwind semantic tokens).
const INPUT_CLASS
  = 'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20'

interface Props extends Pick<AriaAttributes, 'aria-invalid'> {
  // The Google Maps Places API browser key (host app supplies it from its env).
  apiKey: string
  value: string
  onChange: (value: string) => void
  onSelect: (address: ParsedAddress) => void
  id?: string
  name?: string
  placeholder?: string
  className?: string
  required?: boolean
  disabled?: boolean
  onBlur?: () => void
  // Country bias/restriction (ISO 3166-1 alpha-2, lowercase). Omit for worldwide.
  region?: string
  noResultsText?: string
}

const MIN_CHARS = 3
const DEBOUNCE_MS = 250

export function AddressAutocomplete({
  apiKey,
  value,
  onChange,
  onSelect,
  id,
  name,
  placeholder,
  className,
  required,
  disabled,
  onBlur,
  region,
  noResultsText = 'No matches',
  ...rest
}: Props) {
  const listId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const libRef = useRef<PlacesLib | null>(null)
  const tokenRef = useRef<object | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [suggestions, setSuggestions] = useState<GAutocompleteSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  // Load the Places library once. On failure (e.g. no API key) the field stays
  // a plain text input.
  useEffect(() => {
    let active = true
    loadPlacesLibrary(apiKey)
      .then((lib) => {
        if (active)
          libRef.current = lib
      })
      .catch(() => { /* degrade silently to a plain input */ })
    return () => {
      active = false
    }
  }, [apiKey])

  // Close the dropdown when clicking outside.
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (!containerRef.current?.contains(e.target as Node))
        setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  function scheduleFetch(input: string) {
    if (debounceRef.current)
      clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void fetchSuggestions(input), DEBOUNCE_MS)
  }

  async function fetchSuggestions(input: string) {
    const lib = libRef.current
    if (!lib || input.trim().length < MIN_CHARS) {
      setSuggestions([])
      setOpen(false)
      return
    }
    if (!tokenRef.current)
      tokenRef.current = new lib.AutocompleteSessionToken()

    setLoading(true)
    try {
      const { suggestions: results }
        = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken: tokenRef.current,
          ...(region ? { region } : {}),
        })
      setSuggestions(results.filter(s => s.placePrediction))
      setActiveIndex(-1)
      setOpen(true)
    }
    catch {
      setSuggestions([])
      setOpen(false)
    }
    finally {
      setLoading(false)
    }
  }

  async function choose(suggestion: GAutocompleteSuggestion) {
    const prediction = suggestion.placePrediction
    if (!prediction)
      return
    setOpen(false)
    try {
      const place = prediction.toPlace()
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] })
      onSelect(parsePlace(place))
    }
    catch { /* keep whatever the user typed */ }
    finally {
      // Selection ends the billing session — start a fresh token next time.
      tokenRef.current = null
      setSuggestions([])
    }
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0)
      return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => (i + 1) % suggestions.length)
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1))
    }
    else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      void choose(suggestions[activeIndex]!)
    }
    else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-invalid={rest['aria-invalid']}
        className={cn(INPUT_CLASS, className)}
        onChange={(e) => {
          onChange(e.target.value)
          scheduleFetch(e.target.value)
        }}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      {loading && (
        <span
          aria-hidden
          className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
        />
      )}
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-background p-1 text-foreground shadow-md"
        >
          {suggestions.length === 0
            ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  {noResultsText}
                </li>
              )
            : (
                suggestions.map((s, i) => {
                  const text = s.placePrediction?.text?.toString() ?? ''
                  return (
                    <li key={text + i} role="option" aria-selected={i === activeIndex}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={() => void choose(s)}
                        className={cn(
                          'w-full truncate rounded-sm px-3 py-2 text-left text-sm transition-colors',
                          i === activeIndex
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground',
                        )}
                      >
                        {text}
                      </button>
                    </li>
                  )
                })
              )}
        </ul>
      )}
    </div>
  )
}
